import { sumBy, isEmpty } from 'lodash'
import { isTabbed, submittedOrFailedStates } from './orderStates'

import { createOrderParams } from './orderUtils'
import sumUpModifiers from './sumUpPrice'

// retrieves tender display information based on various flags
// or payment types set on the order
export const getTenderInfo = (order, reopenedTabPayments) => {
  const orderParams = createOrderParams(order)
  const orderPayments = orderParams?.payments ?? order?.payments ?? order.paymentInfo ?? []
  const orderFirstPayment = orderPayments.find(
    (payment) => payment?.amountInCents > 0 || (order?.amountInCents === 0 && payment?.amountInCents === 0),
  )

  const lastFour =
    orderParams?.userAttributes?.last_4 ?? orderFirstPayment?.userAttributes?.last_4 ?? orderFirstPayment?.userAttributes?.last4

  const issuer = orderParams?.userAttributes?.freedompayIssuerName ?? orderFirstPayment?.userAttributes?.freedompayIssuerName

  const isSubmittedOrFailed = submittedOrFailedStates.includes(orderParams?.state)

  if (order.reopened && isTabbed(order)) {
    //no payments
    if (!reopenedTabPayments || !reopenedTabPayments.size) {
      return { displayText: 'N/A' }
    }

    //multiple payments
    if (reopenedTabPayments.size > 1) {
      return { displayText: 'Split' }
    }

    //single cc payment get the first payment and format
    const firstPayment = reopenedTabPayments.first()
    const lastFour = firstPayment?.userAttributes?.last4 || ''
    const issuer = firstPayment?.userAttributes?.freedompayIssuerName || ''

    return { displayText: `*${lastFour}`, cardIssuer: issuer?.split(' ')[0] }
  }

  if (order?.isMultiPayment) {
    return { displayText: 'Split' }
  }

  if (order?.isRichCheckout) {
    return { displayText: 'App' }
  }

  //has credit card info
  if (issuer && lastFour) {
    return { displayText: `*${lastFour}`, cardIssuer: issuer?.split(' ')[0] }
  }

  if (isSubmittedOrFailed || (isTabbed(order) && isEmpty(orderFirstPayment))) {
    return { displayText: 'N/A' }
  }

  return { displayText: orderFirstPayment?.shortDescription ?? 'Custom' }
}

// returns a formatted total of the order.
export const totalDisplay = (order) => {
  if (order.hasOwnProperty('amountInCents')) return `$${(order.amountInCents / 100).toFixed(2)}`

  const totalInCents = sumBy(order.itemModels, 'priceInCents')
  return `$${(totalInCents / 100).toFixed(2)}`
}

export const getSubtotalAmountInCents = (order) => {
  const itemModels = order?.itemModels || []

  const subtotalAmountInCents = itemModels.reduce((acc, item) => {
    if (item.refund) {
      return acc
    }

    if (item?.type === 'DonationLineItem') {
      return acc
    }

    //sumUpModifiers already filters refunded modifiers
    const modifiersSubtotal = sumUpModifiers(item.modifiers)

    return acc + item.priceInCents + modifiersSubtotal
  }, 0)

  return subtotalAmountInCents
}

/**
 * Formats an amount in cents to a signed dollar string
 * @param {number} amountInCents - Amount in cents (can be positive or negative)
 * @returns {string} Formatted string like "+$10.02" or "-$10.02"
 */
export const formatSignedAmount = (amountInCents) => {
  if (!amountInCents || amountInCents === 0) return '+$0.00'

  const absoluteAmount = Math.abs(amountInCents)
  const dollars = (absoluteAmount / 100).toFixed(2)
  const sign = amountInCents >= 0 ? '+' : '-'

  return `${sign}$${dollars}`
}

export default formatSignedAmount
